import { useState } from "react";
import type { FormEvent } from "react";
import type { PayoutAward, PayoutAwardSource, PayoutStructure } from "@averagejoesff/shared";
import { upsertPayouts } from "../../api/adminApi";
import { AdminFormStatus } from "../../components/AdminFormStatus";
import { useAdminForm } from "../../hooks/useAdminForm";

interface EditPayoutsFormProps {
  payouts: PayoutStructure | null;
  idToken: string;
  onSaved: (payouts: PayoutStructure) => void;
}

/** One award row as edited in the form; amount stays a string while typing. */
interface Row {
  label: string;
  amount: string;
  description: string;
  source: PayoutAwardSource;
}

function toRows(awards: PayoutAward[]): Row[] {
  return awards.length > 0
    ? awards.map((a) => ({
        label: a.label,
        amount: String(a.amount),
        description: a.description ?? "",
        source: a.source ?? "manual",
      }))
    : [{ label: "", amount: "", description: "", source: "manual" }];
}

function toPayoutAwards(rows: Row[]): PayoutAward[] {
  return rows
    .filter((r) => r.label.trim() !== "")
    .map((r) => ({
      label: r.label.trim(),
      amount: Number(r.amount) || 0,
      description: r.description.trim() || undefined,
      source: r.source,
    }));
}

export function EditPayoutsForm({ payouts, idToken, onSaved }: EditPayoutsFormProps) {
  const [buyIn, setBuyIn] = useState(String(payouts?.buyInPerTeam ?? ""));
  const [pool, setPool] = useState(String(payouts?.totalPool ?? ""));
  const [note, setNote] = useState(payouts?.note ?? "");
  const [rows, setRows] = useState<Row[]>(() => toRows(payouts?.awards ?? []));
  const { status, error, submit } = useAdminForm(
    () =>
      upsertPayouts(
        {
          buyInPerTeam: Number(buyIn) || 0,
          totalPool: Number(pool) || 0,
          awards: toPayoutAwards(rows),
          note: note.trim() || undefined,
        },
        idToken,
      ),
    onSaved,
  );

  function updateRow(index: number, patch: Partial<Row>) {
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  function addRow() {
    setRows((prev) => [...prev, { label: "", amount: "", description: "", source: "manual" }]);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    void submit();
  }

  return (
    <form className="admin-panel__form" onSubmit={handleSubmit}>
      <div className="admin-panel__row">
        <div className="admin-panel__field">
          <label htmlFor="payout-buy-in">Buy-in per team</label>
          <input
            id="payout-buy-in"
            type="number"
            min="0"
            step="1"
            value={buyIn}
            onChange={(e) => setBuyIn(e.target.value)}
          />
        </div>
        <div className="admin-panel__field">
          <label htmlFor="payout-pool">Total prize pool</label>
          <input
            id="payout-pool"
            type="number"
            min="0"
            step="1"
            value={pool}
            onChange={(e) => setPool(e.target.value)}
          />
        </div>
      </div>

      <ul className="admin-panel__team-list">
        {rows.map((row, i) => (
          <li key={i} className="admin-panel__team-row">
            <div className="admin-panel__team-row-fields">
              <input
                type="text"
                placeholder="Award label"
                value={row.label}
                onChange={(e) => updateRow(i, { label: e.target.value })}
              />
              <input
                type="number"
                min="0"
                step="1"
                placeholder="Amount"
                value={row.amount}
                onChange={(e) => updateRow(i, { amount: e.target.value })}
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={row.description}
                onChange={(e) => updateRow(i, { description: e.target.value })}
              />
              <select
                aria-label={`How ${row.label || "this award"}'s winner is decided`}
                value={row.source}
                onChange={(e) => updateRow(i, { source: e.target.value as PayoutAwardSource })}
              >
                <option value="manual">Admin picks the winner</option>
                <option value="blowoutWeekly">Auto: weekly blowout winners</option>
                <option value="seasonStandings">Auto: season standings leader</option>
              </select>
            </div>
            <button
              type="button"
              className="admin-panel__remove"
              aria-label={`Remove ${row.label || "award"}`}
              onClick={() => removeRow(i)}
            >
              ×
            </button>
          </li>
        ))}
      </ul>

      <div className="admin-panel__field">
        <label htmlFor="payout-note">Note (optional)</label>
        <input
          id="payout-note"
          type="text"
          placeholder="e.g. how any leftover balance is spent"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>

      <div className="admin-panel__actions">
        <button type="button" onClick={addRow}>
          + Add award
        </button>
        <button type="submit" disabled={status === "pending"}>
          {status === "pending" ? "Saving…" : "Save payouts"}
        </button>
        <AdminFormStatus
          status={status}
          error={error}
          savedLabel="Saved — other viewers may take up to a minute to see it."
        />
      </div>
    </form>
  );
}
